/*
 * Pretty marketing splash / sign-in page for DataSphere, shown to
 * unauthenticated visitors instead of a bare login button. Already
 * signed-in users are silently resumed and never see this page (see the
 * mount-time "check existing session" effect below). Replicates the
 * sign-in logic of @backstage/core-components's SingleSignInPage, but with
 * fully custom marketing content instead of the default Backstage chrome.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { Progress, UserIdentity } from '@backstage/core-components';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircleOutline';
import StorageIcon from '@material-ui/icons/Storage';
import ChatIcon from '@material-ui/icons/QuestionAnswer';
import PublicIcon from '@material-ui/icons/Public';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import BusinessIcon from '@material-ui/icons/Business';
import { LogoFull } from '../nav/LogoFull';
import { useLandingStyles } from './DataSphereLandingPage.styles';

const OFFERINGS = [
  {
    Icon: PublicIcon,
    title: 'Unity Catalog',
    description:
      'Browse every certified dataset, table and data product Howden has to offer — with ownership, quality status and lineage laid out clearly. Start here to understand what exists and who owns it.',
  },
  {
    Icon: ChatIcon,
    title: 'Databricks Genie',
    description:
      'Ask questions of the data in plain English — no SQL, no code required. Type a question, get an answer. The fastest way to get value from DataSphere.',
  },
];

const TIERS = [
  {
    Icon: BusinessIcon,
    title: 'Group Hub Consumer',
    description:
      'Read and report from Group-certified data without running any infrastructure of your own.',
  },
  {
    Icon: DeviceHubIcon,
    title: 'A Spoke',
    description:
      'A private team workspace for exploration and local analysis — the simplest way to get started, with room to grow.',
  },
  {
    Icon: StorageIcon,
    title: 'Your Own Data Operation',
    description:
      'Ingest your own sources, run production pipelines, and publish certified data products as your needs mature.',
  },
];

const PLATFORM_PROVIDES = [
  'A scoped Databricks workspace with compute, quotas and auto-expiry',
  'Certified gold data shared in via Delta Sharing, at no extra engineering cost',
  'Platform guardrails for security, identity and networking',
  'Onboarding training, documentation and production monitoring',
  'The Group canonical data model and naming conventions',
  'A Unity Catalog metastore with Group-level governance',
];

export const DataSphereLandingPage = ({ onSignInSuccess }: SignInPageProps) => {
  const classes = useLandingStyles();
  const authApi = useApi(microsoftAuthApiRef);
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get('error');

  const [checkingSession, setCheckingSession] = useState(!errorParam);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<Error>();

  const completeSignIn = useCallback(
    async (
      identityResponse: Awaited<
        ReturnType<typeof authApi.getBackstageIdentity>
      >,
    ) => {
      if (!identityResponse) {
        throw new Error('Sign-in did not complete. Please try again.');
      }
      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    },
    [authApi, onSignInSuccess],
  );

  // Silently resume an existing session on mount so a returning,
  // already-authenticated visitor never sees the marketing splash.
  useEffect(() => {
    if (errorParam) {
      return undefined;
    }

    let mounted = true;
    (async () => {
      try {
        const identity = await authApi.getBackstageIdentity({
          optional: true,
        });
        if (mounted && identity) {
          await completeSignIn(identity);
          return;
        }
      } catch {
        // No existing session — fall through to showing the landing page.
      }
      if (mounted) {
        setCheckingSession(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = useCallback(async () => {
    setSigningIn(true);
    setError(undefined);
    try {
      const identity = await authApi.getBackstageIdentity({
        instantPopup: true,
      });
      await completeSignIn(identity);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSigningIn(false);
    }
  }, [authApi, completeSignIn]);

  if (checkingSession) {
    return <Progress />;
  }

  return (
    <div className={classes.root}>
      <Box className={classes.hero}>
        <Container maxWidth="md" className={classes.heroContent}>
          <LogoFull />
          <Typography variant="overline" className={classes.eyebrow}>
            Howden Group Data Platform Engineering
          </Typography>
          <Typography variant="h1" className={classes.heroTitle}>
            DataSphere
          </Typography>
          <Typography variant="h5" className={classes.heroSubtitle}>
            The single home for Howden's certified data — discover it,
            understand it, and build on it.
          </Typography>
          <Button
            variant="contained"
            size="large"
            className={classes.signInButton}
            onClick={handleSignIn}
            disabled={signingIn}
          >
            {signingIn ? 'Signing in…' : 'Sign in with Microsoft'}
          </Button>
          <Typography variant="body2" className={classes.heroHelper}>
            Use your Howden Microsoft account to continue.
          </Typography>
          {(error ?? errorParam) && (
            <Typography
              variant="body2"
              color="error"
              className={classes.errorText}
            >
              {error?.message ?? errorParam}
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" className={classes.section}>
        <Typography
          variant="h3"
          align="center"
          className={classes.sectionTitle}
        >
          What is DataSphere?
        </Typography>
        <Typography
          variant="body1"
          align="center"
          className={classes.sectionIntro}
        >
          A growing catalogue of certified data products covering clients,
          policies, entities and more — discoverable, governed, and ready to
          use.
        </Typography>
        <Grid container spacing={4} className={classes.cardGrid}>
          {OFFERINGS.map(({ Icon, title, description }) => (
            <Grid item xs={12} md={6} key={title}>
              <Card className={classes.card}>
                <CardContent>
                  <Icon className={classes.cardIcon} />
                  <Typography variant="h5" className={classes.cardTitle}>
                    {title}
                  </Typography>
                  <Typography variant="body1">{description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box className={classes.altSection}>
        <Container maxWidth="lg" className={classes.section}>
          <Typography
            variant="h3"
            align="center"
            className={classes.sectionTitle}
          >
            What we offer
          </Typography>
          <Typography
            variant="body1"
            align="center"
            className={classes.sectionIntro}
          >
            DataSphere runs on a hub-and-spoke model, with three levels of
            engagement — from lightweight consumption to a fully federated data
            operation.
          </Typography>
          <Grid container spacing={4} className={classes.cardGrid}>
            {TIERS.map(({ Icon, title, description }) => (
              <Grid item xs={12} md={4} key={title}>
                <Card className={classes.card}>
                  <CardContent>
                    <Icon className={classes.cardIcon} />
                    <Typography variant="h5" className={classes.cardTitle}>
                      {title}
                    </Typography>
                    <Typography variant="body1">{description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box className={classes.providesBox}>
            <Typography variant="h6" className={classes.providesTitle}>
              Every spoke gets, from Group:
            </Typography>
            <Grid container spacing={2}>
              {PLATFORM_PROVIDES.map(item => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={item}
                  className={classes.providesItem}
                >
                  <CheckCircleIcon className={classes.providesIcon} />
                  <Typography variant="body2">{item}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      <Box className={classes.footerCta}>
        <Container maxWidth="sm" className={classes.footerContent}>
          <Typography variant="h4" className={classes.footerTitle}>
            Ready to explore your data?
          </Typography>
          <Button
            variant="contained"
            size="large"
            className={classes.signInButton}
            onClick={handleSignIn}
            disabled={signingIn}
          >
            {signingIn ? 'Signing in…' : 'Sign in with Microsoft'}
          </Button>
          <Typography variant="body2" className={classes.footerHelper}>
            New to DataSphere? Contact the Group Data Operations team to get
            started.
          </Typography>
        </Container>
      </Box>
    </div>
  );
};
